/**
 * Fabric.js v7 patch: reuse each clip path's layer canvas.
 *
 * Fabric clips an object by painting the clip shape into a layer canvas the
 * size of the object's cache, then masking the cache with it, and it makes a
 * new canvas for that layer on every redraw. A playing video redraws on every
 * frame, and both its group and the video inside carry a clip, so nearly all
 * of the clipping time went on allocating canvases, not on drawing shapes.
 * Each clip path now keeps its layer and clears it for the next draw.
 *
 * A layer may be larger than the cache it masks: Fabric draws it at the
 * cache's top left, so only that corner counts. Layers grow in steps, so an
 * object that is scaling does not get a new canvas on every frame.
 *
 * The body is Fabric's own `createClipPathLayer`; only where the canvas comes
 * from differs.
 */
import { FabricObject, util } from 'fabric';
import type { ClipDrawContext, ClipLayerHost } from '@/types/canvas/fabric';

const layers = new WeakMap<FabricObject, CanvasRenderingContext2D>();

const LAYER_STEP_PX = 64;

// a kept layer more than this many times the area needed is made again smaller
const MAX_LAYER_AREA_RATIO = 2;

(FabricObject.prototype as unknown as ClipLayerHost).createClipPathLayer =
  function (
    this: FabricObject,
    clipPath: FabricObject,
    context: ClipDrawContext
  ): HTMLCanvasElement {
    const ctx = clearedLayer(clipPath, context.width, context.height);
    ctx.translate(context.cacheTranslationX, context.cacheTranslationY);
    ctx.scale(context.zoomX, context.zoomY);
    clipPath._cacheCanvas = ctx.canvas;
    for (const parentClipPath of context.parentClipPaths) {
      parentClipPath.transform(ctx);
    }
    context.parentClipPaths.push(clipPath);
    if (clipPath.absolutePositioned) {
      const m = util.invertTransform(this.calcTransformMatrix());
      ctx.transform(m[0], m[1], m[2], m[3], m[4], m[5]);
    }
    clipPath.transform(ctx);
    clipPath.drawObject(ctx, true, context);
    return ctx.canvas;
  };

/**
 * The clip path's layer, at least `width × height`, transparent, with its
 * drawing state back to a fresh context's: that state is saved once when the
 * context is made and restored before each reuse.
 */
function clearedLayer(
  clipPath: FabricObject,
  width: number,
  height: number
): CanvasRenderingContext2D {
  const kept = layers.get(clipPath);
  if (kept && fits(kept.canvas, width, height)) {
    kept.restore();
    kept.clearRect(0, 0, kept.canvas.width, kept.canvas.height);
    kept.save();
    return kept;
  }
  // resizing clears the canvas and resets its context state
  const canvas = kept?.canvas ?? document.createElement('canvas');
  canvas.width = Math.ceil(width / LAYER_STEP_PX) * LAYER_STEP_PX;
  canvas.height = Math.ceil(height / LAYER_STEP_PX) * LAYER_STEP_PX;
  const ctx = canvas.getContext('2d')!;
  ctx.save();
  layers.set(clipPath, ctx);
  return ctx;
}

function fits(canvas: HTMLCanvasElement, width: number, height: number) {
  return (
    canvas.width >= width &&
    canvas.height >= height &&
    canvas.width * canvas.height <= MAX_LAYER_AREA_RATIO * width * height
  );
}
